import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import Checkbox from 'components/forms/Checkbox'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import StepperDialog from 'components/modal/StepperDialog'
import { groupBy, isNaN, isNil, isUndefined, keys, zipObject } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import Icon from 'components/icon/Icon'
import { greyBlue, mainBlue } from 'utils/constants/ColorTheme'
import { StepTaxonThresholds, TableTaxonThreshold } from './TaxonThreshold'
import { StepParameterThresholds, TableParameterThreshold } from './ParameterThreshold'
import UsersStep from 'components/modal/userFilter/UsersStep'
import UsersDisplay from 'components/modal/UsersDisplay'
import { Buffer } from 'buffer'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import HelpImportQualityThreshold from './HelpImportQualityThreshold'
import useSandreList from 'utils/customHook/useSandreList'
import { DEFAULT_DISCRIMINANT, DISCRIMINANT_SIGN, EXPORT_IMPORT_THRESHOLD_HEADERS, THRESHOLD, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import useListIndexed from 'utils/customHook/useListIndexed'
import ToastrAction from 'toastr/actions/ToastrAction'

const STEP_DESCRIPTION = 0
const STEP_SELECT_PARAMETER = 1
const STEP_USER_RIGHT = 2

const Threshold = ({
    threshold = {},
    qualityThreshold = {},
    childrenThresholds = [],
    isEditMode = false,

    toogleActive = () => { },
    onUpdate = () => { },
    onDelete = () => { },
    setQualityThresholds = () => {},
}) => (
    <>
        <Grid size={12}>
            <Accordion>
                <AccordionSummary sx={{ height: undefined }} color={isEditMode ? mainBlue : greyBlue}>
                    <Grid container sx={{ width: '100%' }} alignItems='center' justifyContent='space-between'>
                        <Grid size={0.5}>
                            <Icon
                                icon={threshold.status === 1 ? 'check_box' : 'check_box_outline_blank'}
                                onClick={e => {
                                    if (!isEditMode) {
                                        return
                                    }
                                    e.stopPropagation()
                                    toogleActive(threshold.code)
                                }}
                            />
                        </Grid>
                        <Grid size={3}>
                            <span style={{ fontSize: '1.3rem' }}>
                                {threshold.name}
                            </span>
                        </Grid>
                        <Grid size={5.5}>
                            <span style={{ fontSize: '1.3rem' }}>
                                {threshold.comment}
                            </span>
                        </Grid>
                        <Grid size={1}>
                            <span style={{ fontSize: '1.3rem' }}>
                                {qualityThreshold.thresholds?.length ?? 0} {qualityThreshold.thresholds?.length > 1 ? i18n.thresholds : i18n.threshold}
                            </span>
                        </Grid>
                        <Grid size={1}>
                            {
                                isEditMode && (
                                    <>
                                        <Icon
                                            icon='edit'
                                            tooltip={i18n.edit}
                                            onClick={e => {
                                                e.stopPropagation()
                                                onUpdate(threshold.code)
                                            }}
                                        />
                                        <Icon
                                            icon='delete'
                                            tooltip={i18n.delete}
                                            onClick={e => {
                                                e.stopPropagation()
                                                onDelete(threshold.code)
                                            }}
                                        />
                                    </>
                                )
                            }
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                    {[THRESHOLD.PC, THRESHOLD.INDICE].includes(threshold.thresholdType) && (
                        <TableParameterThreshold
                            threshold={threshold}
                            qualityThresholds={qualityThreshold.thresholds}
                            setQualityThresholds={setQualityThresholds}
                            isEditMode={isEditMode}
                        />
                    )}
                    {threshold.thresholdType === THRESHOLD.TAXON && (
                        <TableTaxonThreshold
                            threshold={threshold}
                            qualityThresholds={qualityThreshold.thresholds}
                            setQualityThresholds={setQualityThresholds}
                            isEditMode={isEditMode}
                        />
                    )}
                </AccordionDetails>
            </Accordion>
        </Grid>
        {
            childrenThresholds.map(child => (
                <Grid container spacing={1} size={12} sx={{ marginLeft: '20px' }} key={child.threshold.code}>
                    <Threshold
                        threshold={child.threshold}
                        qualityThreshold={child.qualityThreshold}
                        childrenThresholds={child.childrens}
                        isEditMode={isEditMode}

                        toogleActive={toogleActive}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                        setQualityThresholds={setQualityThresholds}
                    />
                </Grid>
            ))
        }
    </>
)

Threshold.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    qualityThreshold: PropTypes.shape({
        thresholdCode: PropTypes.number,
        thresholdType: PropTypes.string,
        thresholds: PropTypes.arrayOf(PropTypes.shape({
            parameterCode: PropTypes.string,
            unit: PropTypes.string,
            threshold1: PropTypes.number,
            threshold2: PropTypes.number,
            threshold3: PropTypes.number,
            threshold4: PropTypes.number,
        })),
    }),
    // eslint-disable-next-line react/forbid-prop-types
    childrenThresholds: PropTypes.array,
    /*
    ----- recursive props -----
    childrenThresholds = [
        {
            threshold: {},
            qualityThreshold: {},
            childrenThresholds: [],
        },
        ...
    ]
    */
    isEditMode: PropTypes.bool,

    toogleActive: PropTypes.func,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    setQualityThresholds: PropTypes.func,
}

const StepDescription = ({
    threshold = {},
    setThreshold = () => {},
}) => {
    const {
        thresholds,
    } = useSelector(store => ({
        thresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const thresholdTypes = useMemo(() => {
        return [
            { id: THRESHOLD.PC, label: i18n.PCparameters },
            { id: THRESHOLD.TAXON, label: i18n.taxons },
            { id: THRESHOLD.INDICE, label: i18n.indices },
            // { id: SELECTION.ENVIRONMENTAL_PARAMETER, label: i18n.environmentalParameters },
            // { id: SELECTION.STATE, label: i18n.states },
        ]
    }, [])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Input
                            title={i18n.name}
                            value={threshold.name}
                            onChange={v => setThreshold(th => ({ ...th, name: v || undefined }))}
                            maxLength={100}
                            obligatory
                        />
                    </Grid>
                    <Grid size={2}>
                        <Checkbox
                            label={i18n.active}
                            checked={threshold.status === 1}
                            componentClassName='padding-top-1'
                            onChange={v => setThreshold(th => ({ ...th, status: v ? 1 : 0 }))}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={thresholdTypes}
                            value={threshold.thresholdType}
                            noNullValue
                            label={i18n.type}
                            onChange={v => setThreshold(th => ({ ...th, thresholdType: isUndefined(v) ? v : `${v}` }))}
                            disabled={!isUndefined(threshold.code)}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Input
                            title={i18n.comment}
                            value={threshold.comment}
                            onChange={v => setThreshold(th => ({ ...th, comment: v }))}
                        />
                    </Grid>
                    <Grid size={8}>
                        <Select
                            options={thresholds.filter(t => !t.referenceCode && t.code !== threshold.code)}
                            label={i18n.parentThreshold}
                            value={threshold.referenceCode}
                            nullLabel='&nbsp;'
                            onChange={v => setThreshold(th => ({ ...th, referenceCode: isUndefined(v) ? v : `${v}` }))}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

StepDescription.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    setThreshold: PropTypes.func,
}

const StepUserRight = ({
    threshold = {},
    setThreshold = () => {},
}) => {
    const {
        users,
    } = useSelector(store => ({
        users: store.UserReducer.users,
    }), shallowEqual)

    const [userOption, setUserOption] = useState(threshold.users.length === 0 && threshold.administrators.length === 0 ? 0 : 1)

    const filteredUsers = users.filter(u => threshold.users.some(l => l === u.login))

    return (
        <UsersStep
            stateUsers={filteredUsers}
            setStateUsers={newUser => setThreshold(th => ({ ...th, users: newUser.map(u => u.login) }))}

            stateAdministrators={threshold.administrators}
            setStateAdministrators={newAdministrators => setThreshold(th => ({ ...th, administrators: newAdministrators }))}

            userOption={userOption}
            setUserOption={(v) => {
                setUserOption(v)
                setThreshold(th => ({ ...th, users: [], administrators: [] }))
            }}
        >
            <UsersDisplay
                userOption={userOption}
                stateAdministrators={threshold.administrators}
                stateUsers={filteredUsers}
            />
        </UsersStep>
    )
}

StepUserRight.propTypes = {
    threshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
        user: PropTypes.arrayOf(PropTypes.string),
        administrators: PropTypes.arrayOf(PropTypes.number),
    }),
    setThreshold: PropTypes.func,
}

const StepperThreshold = ({
    isOpen = false,
    onClose = () => { },
    onSave = () => { },

    selectedThreshold = {},
    selectedQualityThreshold = {},
    thresholds = [],
}) => {
    const [threshold, setThreshold] = useState({})
    const [qualityThresholds, setQualityThresholds] = useState([])

    const dispatch = useDispatch()

    useEffect(() => {
        if (isOpen) {
            setThreshold(selectedThreshold)
            setQualityThresholds(selectedQualityThreshold.thresholds ?? [])
        }
    }, [selectedQualityThreshold, selectedThreshold, isOpen])

    const isThresholdComplete = useMemo(() => {
        if (isUndefined(qualityThresholds)) {
            return true
        }
        const groupThresholds = groupBy(qualityThresholds, th => `${th.parameterCode}:${th.unit}:${th.station}:${th.fraction}`)
        const hasDuplicate = keys(groupThresholds).some(key => groupThresholds[key].length > 1)
        const isThresholdMissing = qualityThresholds.some(t => isUndefined(t.threshold1))
        return !hasDuplicate && !isThresholdMissing
    }, [qualityThresholds])

    const unavailableThresholds = thresholds.map(t => t.name).filter(t => t !== selectedThreshold.name)

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.description,
                    constant: STEP_DESCRIPTION,
                    nextAvailable: !isUndefined(threshold.name) && !unavailableThresholds.includes(threshold.name),
                },
                {
                    label: i18n.thresholds,
                    constant: STEP_SELECT_PARAMETER,
                    nextAvailable: isThresholdComplete,
                },
                {
                    label: i18n.users,
                    constant: STEP_USER_RIGHT,
                },
            ]}
            open={isOpen}
            title={isUndefined(threshold.code) ? i18n.newThreshold : i18n.updateThreshold}
            closeDialog={onClose}
            renderSaveButton={step => (step === STEP_USER_RIGHT) && (
                <Button
                    onClick={() => {
                        const qualityThreshold = {
                            thresholdCode: threshold.code,
                            thresholdType: threshold.thresholdType,
                            thresholds: qualityThresholds,
                        }
                        dispatch(ToastrAction.info(i18n.rememberToSaveYourChanges))
                        onSave(threshold, qualityThreshold)
                    }}
                    variant='contained'
                >
                    {isUndefined(threshold.code) ? i18n.add : i18n.validate}
                </Button>
            )}
            contentStyle={{ padding: '0 12' }}
            maxWidth='xl'
        >
            {
                step => (
                    <>
                        {step === STEP_DESCRIPTION && (
                            <StepDescription
                                threshold={threshold}
                                setThreshold={setThreshold}
                            />
                        )}
                        {step === STEP_SELECT_PARAMETER && [THRESHOLD.PC, THRESHOLD.INDICE].includes(threshold.thresholdType) && (
                            <StepParameterThresholds
                                qualityThresholds={qualityThresholds}
                                setQualityThresholds={setQualityThresholds}
                            />
                        )}
                        {step === STEP_SELECT_PARAMETER && threshold.thresholdType === THRESHOLD.TAXON && (
                            <StepTaxonThresholds
                                qualityThresholds={qualityThresholds}
                                setQualityThresholds={setQualityThresholds}
                            />
                        )}
                        {step === STEP_USER_RIGHT && (
                            <StepUserRight
                                threshold={threshold}
                                setThreshold={setThreshold}
                            />
                        )}
                    </>
                )
            }
        </StepperDialog>
    )
}

StepperThreshold.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    selectedThreshold: PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    }),
    selectedQualityThreshold: PropTypes.shape({
        thresholdCode: PropTypes.number,
        thresholdType: PropTypes.string,
        thresholds: PropTypes.arrayOf(PropTypes.shape({
            parameterCode: PropTypes.string,
            unit: PropTypes.string,
            station: PropTypes.number,
            threshold1: PropTypes.number,
            threshold2: PropTypes.number,
            threshold3: PropTypes.number,
            threshold4: PropTypes.number,
            calciumClass: PropTypes.number,
            frType: PropTypes.number,
            fishCode: PropTypes.number,
        })),
    }),
    thresholds: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
        comment: PropTypes.string,
        status: PropTypes.number,
    })),
}

const PopinImportThresholds = ({
    isOpen = false,
    close = () => {},
    newId = 1,
    onValidate = () => {},
}) => {
    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const indexedQualitometers = useListIndexed(qualitometers, 'code')

    const lexiconFRType = useSandreList('QUALITOMETRES.TYPE_FR.RIVIERE')

    const [fileName, setFileName] = useState()

    const [thresholds, setThresholds] = useState([])
    const [qualityThresholds, setQualityThresholds] = useState([])

    useEffect(() => {
        if (isOpen) {
            setThresholds([])
            setQualityThresholds([])
            setFileName()
        }
    }, [isOpen])

    const openFile = e => {
        const reader = new FileReader()
        const fileOpened = e.target.files[0]

        if (isUndefined(fileOpened)) {
            return
        }

        const CORRESPONDING_THRESHOLD_TYPE = {
            [i18n.increasing]: THRESHOLD_TYPE.INCREASING,
            [i18n.decreasing]: THRESHOLD_TYPE.DECREASING,
            [i18n.bounded]: THRESHOLD_TYPE.BOUNDED,
        }
        const CORRESPONDING_THRESHOLD_DISPLAY = {
            [i18n.increasing]: THRESHOLD_DISPLAY.INCREASING,
            [i18n.decreasing]: THRESHOLD_DISPLAY.DECREASING,
        }

        reader.onload = upload => {
            const split = upload.target.result.split(',')
            if (split[0].includes('text/csv') || split[0].includes('vnd.ms-excel')) {
                const buf = Buffer.from(split[1], 'base64').toString()
                const [, ...lines] = buf.split('\n').map(line => line.replace(/(\r|")/g, '').replace(/(,)/g, '.'))

                const parsedLines = lines.map(line => {
                    const cells = line.split(';').map(cell => cell || undefined)
                    return zipObject(EXPORT_IMPORT_THRESHOLD_HEADERS.map(h => h.key ?? h), cells)
                })

                const filteredLines = parsedLines.filter(l => !isNil(l.thresholdCode) && !isNil(l.thresholdName) && !isNil(l.type) && !isNil(l.threshold1) && !isNaN(parseFloat(l.threshold1)))

                const groupedLine = groupBy(filteredLines, t => t.thresholdCode.slice(0, 100))
                const newThresholdCodes = keys(groupedLine)

                const newThresholds = newThresholdCodes.map((thresholdCode, i) => {
                    const { thresholdName, type } = groupedLine[thresholdCode][0]
                    return {
                        code: newId + i,
                        comment: thresholdName,
                        name: thresholdCode,
                        thresholdType: THRESHOLD[type],
                        status: 1,
                        users: [],
                        administrators: [],
                    }
                })

                const newQualityThreshold = newThresholdCodes.map((thresholdCode, i) => {
                    const { type } = groupedLine[thresholdCode][0]
                    return {
                        thresholdCode: newId + i,
                        thresholdType: THRESHOLD[type],
                        name: thresholdCode,
                        thresholds: groupedLine[thresholdCode].map(t => {
                            const thresholdType = CORRESPONDING_THRESHOLD_TYPE[t.thresholdType] ?? THRESHOLD_TYPE.INCREASING
                            const display = CORRESPONDING_THRESHOLD_DISPLAY[t.display] ?? THRESHOLD_DISPLAY.INCREASING
                            const threshold2 = parseFloat(t.threshold2)
                            const threshold3 = t.threshold2 ? parseFloat(t.threshold3) : undefined
                            const threshold4 = t.threshold2 && t.threshold3 ? parseFloat(t.threshold4) : undefined
                            return {
                                parameterCode: t.codeParameterOrTaxon,
                                unit: t.codeUnit,
                                station: indexedQualitometers[t.stationCode]?.id,
                                fraction: t.fractionCode,
                                threshold1: parseFloat(t.threshold1),
                                threshold2: !isNaN(threshold2) ? threshold2 : undefined,
                                threshold3: !isNaN(threshold3) ? threshold3 : undefined,
                                threshold4: !isNaN(threshold4) ? threshold4 : undefined,
                                listType: THRESHOLD[type],
                                thresholdType,
                                display,
                                discriminant: DISCRIMINANT_SIGN[t.discriminant] ?? DEFAULT_DISCRIMINANT[thresholdType],
                                calciumClass: t.hardnessCode ? parseInt(t.hardnessCode) : undefined,
                                frType: lexiconFRType.find(l => l.name === t.frType)?.code,
                                fishCode: t.fishContextCode ? parseInt(t.fishContextCode) : undefined,
                            }
                        }),
                    }
                })

                setFileName(fileOpened.name)
                setThresholds(newThresholds)
                setQualityThresholds(newQualityThreshold)
            } else {
                this.props.toastError(i18n.theSelectedFileMustBeInCsvFormat)
            }
        }
        reader.readAsDataURL(fileOpened)
    }

    const thresholdsFormatted = useMemo(() => {
        const parentsThresholds = thresholds.filter(th => isUndefined(th.referenceCode))
        const formatThreshold = th => {
            const qualityThreshold = qualityThresholds.find(qt => qt.thresholdCode === th.code)
            const childrens = thresholds.filter(child => child.referenceCode === `${th.code}`)
            return {
                threshold: th,
                childrens: childrens.map(formatThreshold),
                qualityThreshold,
            }
        }
        return parentsThresholds.map(formatThreshold)
    }, [qualityThresholds, thresholds])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.threshold}
                onClose={close}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Stack spacing={1}>
                    <Card>
                        <CardContent>
                            <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                                <Grid size={3}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        component='label'
                                        fullWidth
                                    >
                                        <span>{i18n.importFile} {i18n.csv}</span>
                                        <input
                                            type='file'
                                            accept='.csv, .CSV'
                                            hidden
                                            onChange={openFile}
                                        />
                                    </Button>
                                </Grid>
                                <Grid size={8.5}>
                                    <label style={{ fontSize: '1em' }}>{fileName}</label>
                                </Grid>
                                <Grid size={0.5}>
                                    <HelpImportQualityThreshold />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent>
                            <Grid container spacing={1}>
                                {!thresholds.length && (
                                    <Grid>
                                        <h5 className='padding-1'>{i18n.noThresholds}</h5>
                                    </Grid>
                                )}
                                {thresholdsFormatted.map(({ threshold, childrens, qualityThreshold }) => (
                                    <Threshold
                                        key={threshold.code}
                                        threshold={threshold}
                                        qualityThreshold={qualityThreshold}
                                        childrenThresholds={childrens}
                                    />
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Grid container alignItems='center' justifyContent='right' columnSpacing={2} style={{ padding: '0 10' }}>
                    <Grid container size={3} justifyContent='flex-end'>
                        <Button
                            onClick={() => {
                                onValidate(thresholds, qualityThresholds)
                            }}
                            variant='contained'
                            color='primary'
                        >
                            {i18n.validate}
                        </Button>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

PopinImportThresholds.propTypes = {
    isOpen: PropTypes.bool,
    newId: PropTypes.number,
    close: PropTypes.func,
    onValidate: PropTypes.func,
}

export {
    Threshold,
    StepperThreshold,
    PopinImportThresholds,
}